import { isLayeredShape, tileConfigurationKey, isSameColor } from "~/shared/layeredEngraving";
import { defineStore } from 'pinia'
import { calculateMultiShapePositions, get3dShapeGroup, sort3dShapeList } from '~/components/CanvasGrid/helpers'
import { DEFAULT_GRID_HEIGHT, DEFAULT_GRID_PATTERN, DEFAULT_GRID_WIDTH, FOOT_IN_PIXELS, GRID_PATTERN_IDS, GRID_PATTERN_LIST, GRID_START_OFFSET, INCH_IN_PIXELS, MAX_GRID_HEIGHT_PX, MAX_GRID_WIDTH_PX, SELECTABLE_TOOLS } from '~/composables/grid/constants'
import { computeRequiredGridSize, type GridGrowthResult, type PlacementFootprintCell } from '~/composables/grid/placement'
import { type ColorChoice, isWoodGrainColor } from '~/composables/shapes/colors'
import { COLOR_CHOICES } from '~/composables/shapes/constants'
import { isBlank3DBox } from '~/composables/shapes/shapesFor3D'
// Note: COLOR_CHOICES kept as fallback for SSR/store init; use useStudioColors() for runtime
import { getShapeBaseConfig } from '~/composables/shapes/factory'
import { isRotationLocked, normalizeShapeRotation } from '~/composables/shapes/shapeCapabilities'
import { getVariationCatalogRevision } from '~/composables/shapes/variationMatching'
import { transformShapeForAdd } from '~/composables/shapes/transform'
import { convertNominalToActualSize, generateLegendID, getShapeById, getShapeListBounds } from '~/composables/shapes/utils'
import { mockUpdatableShapeAttrs, type GridClipboard, type GridPattern, type Line, type Shape, type ShapeInterface, type ShapeMetadata, type UpdatableShapeAttrs, type Vector2d } from '~/types/grid.types'

export const useGridStore = defineStore('grid', {
  state: () => ({
    gridPattern: null as GridPattern | null,
    // Plain state on purpose: nothing persists it, so 1 in placement snap resets to 3 in on every load.
    authorEmail: null as string | null, // todo: can we move it from here?
    gridWidth: DEFAULT_GRID_WIDTH,
    gridHeight: DEFAULT_GRID_HEIGHT,
    gridZoom: 1 as number,
    // Whether the canvas vertical scrollbar is currently shown. Used by the
    // studio shell to shift the tutorial arrow clear of the scrollbar.
    verticalScrollbarVisible: false as boolean,
    gridPos: {x: 20, y: 20} as Vector2d,
    gridBgColor: 'white' as string,
    startOffset: GRID_START_OFFSET,
    footInPx: FOOT_IN_PIXELS,
    selectedShape: null as Shape | null,
    selectedRotation: null as number | null,
    activeLayer: 'top' as 'top' | 'bottom',
    layeredBottomColor: null as ColorChoice | null,
    selectedColor: COLOR_CHOICES[0] as ColorChoice | null,
    selectedTool: SELECTABLE_TOOLS.MOVE_GRID as SELECTABLE_TOOLS | null,
    shapeList: [] as Array<ShapeInterface>,
    tempNewItems: [] as Array<ShapeInterface>,
    selectedItemsOnGrid: [] as Array<ShapeInterface>,
    undoStack: [] as Array<ShapeInterface[]>,
    redoStack: [] as Array<ShapeInterface[]>,
    lassoSelectionItem: null as Line | null,
    rectSelectionItem: null as Line | null,
    clipboard: { action: null, items: [] } as GridClipboard,
    isProcessing: false as boolean,
    designWidth: '0' as string,
    designHeight: '0' as string,
    tempItemCache: null as ShapeInterface | null, // Cache for temp items to avoid recreating
    lastTempItemKey: '' as string,
    tempRequestRevision: 0, // Key to track if temp item config changed
    legendScopeId: null as string | null,
    // Grid edge expansion state
    hoveredEdges: [] as string[], // Which edges are currently being hovered
    isResizingGrid: false as boolean, // Whether grid is being resized via drag
    resizeStartPos: { x: 0, y: 0 } as Vector2d, // Mouse position when resize started
    resizeStartSize: { width: 0, height: 0 } as { width: number, height: number }, // Grid size when resize started
    showDepthPreview: false as boolean,
    shapeList3D: [] as Array<any>,
  }),

  getters: {
    gridSize: state => ({
      width: {
        ft: Math.floor(state.gridWidth / FOOT_IN_PIXELS),
        in: (state.gridWidth % FOOT_IN_PIXELS) / INCH_IN_PIXELS,
      },
      height: {
        ft: Math.floor(state.gridHeight / FOOT_IN_PIXELS),
        in: (state.gridHeight % FOOT_IN_PIXELS) / INCH_IN_PIXELS,
      }
    }),
    shapesWithLegends: state => {
      const variantMap = new Map<string, string>()
      const assignedLegendIds = new Set<string>()
      const legendScopeId = state.legendScopeId || null

      return state.shapeList.map(shape => {
        const variantId = tileConfigurationKey(shape.metadata || shape.attrs?.metadata) || 'unknown'

        if (!variantMap.has(variantId)) {
          let reroll = 0
          let legendId = generateLegendID(variantId, legendScopeId, reroll)

          while (assignedLegendIds.has(legendId)) {
            reroll += 1
            legendId = generateLegendID(variantId, legendScopeId, reroll)
          }

          assignedLegendIds.add(legendId)
          variantMap.set(variantId, legendId)
        }

        return {
          ...shape,
          draggable: false,
          metadata: {
            ...shape.metadata,
            legend_id: variantMap.get(variantId)
          } as ShapeMetadata
        }
      })
    },
    designArea: state => {
      function calcArea(attrs: any) {
        let w = attrs.width
        let h = attrs.height

        if (['rect-triangle', 'triangle'].includes(attrs.type)) {
          w = w / 2
        }

        if (attrs.type === 'arc') {
          w = h = attrs.outerRadius

          if (attrs.angle === 180) {
            w = attrs.outerRadius * 2
          }
        }

        return (w / FOOT_IN_PIXELS) * (h / FOOT_IN_PIXELS)
      }

      // todo: find a better way to ensure width and height
      const areaFt = state.shapeList.reduce((acc, shape) => acc + calcArea({ ...shape, ...shape.attrs }), 0)

      return `${areaFt.toFixed(2)}ft²`
    },
    designSize: state => {
      const starts = { minX: state.gridWidth, minY: state.gridHeight, maxX: 0, maxY: 0 }

      const result = convertNominalToActualSize(state.shapeList)
      const convertedList = result[0] as any[]
      const bounds = getShapeListBounds(convertedList, starts)


      let actualWidth = Math.max(bounds.maxX - bounds.minX, 0)
      let actualHeight = Math.max(bounds.maxY - bounds.minY, 0)

      const calcWidthIn = (actualWidth / INCH_IN_PIXELS).toFixed(2)
      const calcHeightIn = (actualHeight / INCH_IN_PIXELS).toFixed(2)


      state.designWidth = calcWidthIn
      state.designHeight = calcHeightIn
      return `${calcWidthIn}in x ${calcHeightIn}in`
    },
    placementSnap: (state) => state.gridPattern?.fineSnap ?? state.gridPattern?.snap,
    // Shift+arrow nudges by the coarse 3 in snap.
    arrowNudgeStep: (state) => (coarse: boolean) => {
      if (coarse) return state.gridPattern?.snap
      return state.gridPattern?.fineSnap ?? state.gridPattern?.snap
    },
  },

  actions: {
    updateSelectedTool(tool: SELECTABLE_TOOLS) {
      this.selectedTool = tool
    },
    setLegendScopeId(scopeId: string | null) {
      this.legendScopeId = scopeId
    },
    getTilesList() {
      const list = new Map<string, ShapeMetadata>()
      const is3DGrid = this.gridPattern?.id === GRID_PATTERN_IDS.THREE_D

      // First pass: count tiles per variant
      this.shapesWithLegends.forEach(shape => {
        if (!shape.metadata) return

        const key = tileConfigurationKey(shape.metadata)
        const found = list.get(key)
        if (found) {
          found.quantity = (found.quantity || 0) + 1
        } else {
          list.set(key, { ...shape.metadata, quantity: 1 })
        }
      })

      // For 3D grid, convert tile counts to set quantities
      // The price in metadata is per-SET, not per-tile
      {
        list.forEach((metadata, key) => {
          const shapeDef = getShapeById(metadata.shape_id)
          const tilesPerSet = metadata.layered_engraving?.tiles_per_set || (is3DGrid ? shapeDef?.quantity || 1 : 1)
          const tileCount = metadata.quantity || 1
          // Calculate number of sets needed (round up for partial sets)
          metadata.quantity = Math.ceil(tileCount / tilesPerSet)
        })
      }

      return Array.from(list.values())
    },

    setGridPattern(pattern: GridPattern) {
      this.gridPattern = pattern
    },

    setSelectedShape(shape: Shape) {
      if (isLayeredShape(shape) && !isLayeredShape(this.selectedShape)) {
        this.selectedColor = useStudioColors().getColors().find((c: ColorChoice) => c.label === 'Ebony') || COLOR_CHOICES[0]
        this.activeLayer = 'top'
      }
      this.selectedShape = shape
      // Wood grain patterns are only supported on blank 3D boxes.
      // If an engraved 3D box or any non-blank shape is selected while a wood grain color
      // was active, revert to a regular felt color so sides and front use standard color.
      if (this.selectedColor && isWoodGrainColor(this.selectedColor.label)) {
        if (!isBlank3DBox(shape?.prefix)) {
          const regularColor = useStudioColors()
            .getColors()
            .find((c: ColorChoice) => !isWoodGrainColor(c.label) && c.active) || COLOR_CHOICES[0]
          if (regularColor) this.selectedColor = regularColor
        }
      }
    },

    setSelectedRotation(rotation: number) {
      this.selectedRotation = rotation
    },

    setSelectedColor(color: ColorChoice | null) {
      this.selectedColor = color
    },

    unselectItemsOnGrid() {
      this.selectedItemsOnGrid = []
      this.shapeList.forEach((shape) => {
        shape.dash = undefined
        shape.dashOffset = undefined
        shape.strokeWidth = undefined
        shape.stroke = undefined
      })
      this.updateShapeList3D()
    },

    restoreSelection(selectedIds: string[]) {
      // Clear current selection first
      this.unselectItemsOnGrid()

      // Find shapes that still exist and restore their selection
      const shapesToSelect = this.shapeList.filter(shape => selectedIds.includes(shape.id))

      if (shapesToSelect.length > 0) {
        shapesToSelect.forEach(shape => {
          shape.dash = undefined
          shape.dashOffset = undefined
          shape.strokeWidth = 4
          shape.stroke = '#313558'
        })

        this.selectedItemsOnGrid = shapesToSelect
        this.updateMenuSelection()
      }
    },

    updateMenuSelection() {
      if (this.selectedItemsOnGrid.length < 1) return

      try {
          // todo: fix the interface. move all to .attrs
        const metadata = this.selectedItemsOnGrid[0].metadata || this.selectedItemsOnGrid[0].attrs?.metadata
        const colorChoice = useStudioColors().getColors().find((c: ColorChoice) => isSameColor(c.label, metadata.color_label))
        const shapeChoice = getShapeById(metadata.shape_id)

        if (shapeChoice) this.setSelectedShape(shapeChoice)
        if (colorChoice) this.setSelectedColor(colorChoice)
        if (metadata.layered_engraving) {
          const layer = metadata.layered_engraving
          this.layeredBottomColor = useStudioColors().getColors().find((c: ColorChoice) => isSameColor(c.label, layer.bottom_color)) || { label: layer.bottom_color, hex: layer.bottom_hex, image: '' }
        }

      } catch (e) {
        console.log('Unable to update menu', e)
      }
    },

    selectItemsOnGrid(itemIds: string[]) {
      // todo: attention to measure the cost of all these ops
      this.unselectItemsOnGrid()

      if(itemIds.length < 1) return

      for (const i in itemIds) {
        const itemOnList = this.shapeList.find(shape => shape.id === itemIds[i])
        if (!itemOnList) continue

        itemOnList.dash = undefined
        itemOnList.dashOffset = undefined
        itemOnList.strokeWidth = 4
        itemOnList.stroke = '#313558'

        this.selectedItemsOnGrid.push(itemOnList)
      }

      this.updateMenuSelection()
      this.updateShapeList3D()

      return this.selectedItemsOnGrid
    },

    selectAllItemsOnGrid() {
      const allIds = this.shapeList.map(shape => shape.id)
      return this.selectItemsOnGrid(allIds)
    },

    deleteSelectedItemsOnGrid() {
      if (!this.selectedItemsOnGrid.length) return

      this.isProcessing = true

      // Collect all set_ids from selected items
      const setIdsToDelete = new Set<string>()
      const selectedIds = new Set(this.selectedItemsOnGrid.map(item => item.id))

      this.selectedItemsOnGrid.forEach(item => {
        const setId = item.metadata?.set_id || item.attrs?.metadata?.set_id
        if (setId) {
          setIdsToDelete.add(setId)
        }
      })

      // Find all items in sets that need to be deleted
      const idsToDelete = new Set(selectedIds)
      setIdsToDelete.forEach(setId => {
        this.shapeList.forEach(shape => {
          const shapeSetId = shape.metadata?.set_id || shape.attrs?.metadata?.set_id
          if (shapeSetId === setId) {
            idsToDelete.add(shape.id)
          }
        })
      })

      // Delete all items (selected items + all items in their sets)
      this.shapeList = this.shapeList.filter(shape => !idsToDelete.has(shape.id))
      this.updateShapeList3D()
      this.isProcessing = false

      this.selectedItemsOnGrid = []

      // Save the changes to the backend
      const projectStore = useProjectStore()
      projectStore.save(false, true)
    },

    moveItemToTop(id: string) {
      if (!id) return

      this.isProcessing = true
      const item = this.shapeList.find(i => i.id === id)
      const list = this.shapeList.filter(i => i.id !== id)
      this.isProcessing = false
      if (!item) return
      list.push(item)
      this.shapeList = list
      this.updateShapeList3D()
    },

    moveItemsToFront(ids: string[]) {
      if (!ids.length) return

      this.isProcessing = true
      const items = this.shapeList.filter(i => ids.includes(i.id))
      const remainingItems = this.shapeList.filter(i => !ids.includes(i.id))

      this.shapeList = [...remainingItems, ...items]
      this.updateShapeList3D()
      this.isProcessing = false
    },

    moveItemsToBack(ids: string[]) {
      if (!ids.length) return

      this.isProcessing = true
      const items = this.shapeList.filter(i => ids.includes(i.id))
      const remainingItems = this.shapeList.filter(i => !ids.includes(i.id))

      this.shapeList = [...items, ...remainingItems]
      this.updateShapeList3D()
      this.isProcessing = false
    },

    getUpdatedItem(shape: ShapeInterface, attrs: Partial<UpdatableShapeAttrs>) {
      // Filter to only valid attributes (excluding ID which can conflict)
      const isValidKey = (key: string): key is keyof UpdatableShapeAttrs => {
        return key in (mockUpdatableShapeAttrs as UpdatableShapeAttrs)
      }
      const validAttrs = Object.fromEntries(
        Object.entries(attrs).filter(([key]) => isValidKey(key))
      )

      // Get existing metadata from shape (check both possible locations)
      const existingMetadata = shape.metadata || shape.attrs?.metadata

      // Extract nested attrs if provided (e.g., { attrs: { rotation: 90 } })
      const nestedAttrs = validAttrs.attrs || {}
      const nestedMetadata = nestedAttrs.metadata

      // Get new metadata from top-level or nested attrs
      const newMetadata = validAttrs.metadata || nestedMetadata

      // Merge metadata: preserve existing fields (like set_id), override with new values
      const mergedMetadata = newMetadata
        ? (existingMetadata ? { ...existingMetadata, ...newMetadata } : newMetadata)
        : existingMetadata

      // Build updated attrs object
      // Start with existing shape.attrs, then merge in nested attrs (excluding metadata), then top-level validAttrs
      const { metadata: _, ...nestedAttrsWithoutMetadata } = nestedAttrs
      const { attrs: __, metadata: ___, ...topLevelAttrs } = validAttrs

      // Build attrs, preserving existing metadata if no new metadata provided
      const updatedAttrs: any = {
        ...(shape.attrs || {}),
        ...nestedAttrsWithoutMetadata,
        ...topLevelAttrs
      }

      // Set metadata if we have merged metadata, otherwise preserve existing
      if (mergedMetadata) {
        updatedAttrs.metadata = { ...mergedMetadata }
      } else if (shape.attrs?.metadata) {
        updatedAttrs.metadata = { ...shape.attrs.metadata }
      }

      // Build result: start with shape, apply top-level attrs (excluding attrs and metadata), then set updated attrs
      const result: any = {
        ...shape,
        ...topLevelAttrs,
        attrs: updatedAttrs
      }

      // Set metadata at root level if we have merged metadata or preserve existing
      if (mergedMetadata) {
        result.metadata = { ...mergedMetadata }
      } else if (shape.metadata) {
        result.metadata = { ...shape.metadata }
      }

      return result
    },

    updateItemOnList(id: string, attrs: Partial<UpdatableShapeAttrs>) {
      this.isProcessing = true

      // Find the item being updated to check if it's part of a set
      const targetItem = this.shapeList.find(shape => shape.id === id)
      if (!targetItem) {
        this.isProcessing = false
        return
      }

      // Get set_id from metadata (check both metadata locations)
      const setId = targetItem.metadata?.set_id || targetItem.attrs?.metadata?.set_id

      // If part of a set, find all items in the same set
      const setItemIds = setId
        ? this.shapeList
            .filter(shape => {
              const shapeSetId = shape.metadata?.set_id || shape.attrs?.metadata?.set_id
              return shapeSetId === setId
            })
            .map(shape => shape.id)
        : [id]

      // Separate position and rotation attributes from other attributes
      // Position and rotation are spatial properties that only apply to the specific item being updated, not the other items in the set
      const { x, y, rotation, ...otherAttrs } = attrs as any
      const nonSpatialAttrs: any = { ...otherAttrs }
      if (nonSpatialAttrs.attrs) {
        const { rotation: _r, ...restAttrs } = nonSpatialAttrs.attrs
        nonSpatialAttrs.attrs = restAttrs
      }
      if (nonSpatialAttrs.metadata?.shape_id) {
        const { shape_id: _s, ...restMeta } = nonSpatialAttrs.metadata
        nonSpatialAttrs.metadata = restMeta
      }
      if (nonSpatialAttrs.attrs?.metadata?.shape_id) {
        const { shape_id: _s, ...restMeta } = nonSpatialAttrs.attrs.metadata
        nonSpatialAttrs.attrs = {
          ...nonSpatialAttrs.attrs,
          metadata: restMeta,
        }
      }

      // Update all items in the set with non-spatial attributes (e.g. shared color)
      const tempList = this.shapeList.map((shape) => {
        if (setItemIds.includes(shape.id)) {
          // For the target item, apply all attributes (including position and rotation)
          if (shape.id === id) {
            shape = this.getUpdatedItem(shape, attrs)
          } else {
            // For other items in the set, only apply non-spatial attributes
            shape = this.getUpdatedItem(shape, nonSpatialAttrs)
          }
        }
        return shape
      })

      this.shapeList = tempList
      this.updateShapeList3D()

      // Update selectedItemsOnGrid to reference the new shape objects
      this.selectedItemsOnGrid = this.selectedItemsOnGrid.map(selectedItem => {
        const updatedShape = this.shapeList.find(shape => shape.id === selectedItem.id)
        return updatedShape || selectedItem
      })

      // Move all items in the set to top
      setItemIds.forEach(itemId => this.moveItemToTop(itemId))
      this.isProcessing = false
    },

    updateBatchOnList(list: {id: string, attrs: Partial<UpdatableShapeAttrs>}[]) {
      this.isProcessing = true

      // Collect all set_ids from items being updated
      const setIdsToUpdate = new Set<string>()
      list.forEach(item => {
        const shape = this.shapeList.find(s => s.id === item.id)
        if (shape) {
          const setId = shape.metadata?.set_id || shape.attrs?.metadata?.set_id
          if (setId) {
            setIdsToUpdate.add(setId)
          }
        }
      })

      // Find all items in sets that need to be updated
      const setItemIds = new Set<string>()
      setIdsToUpdate.forEach(setId => {
        this.shapeList.forEach(shape => {
          const shapeSetId = shape.metadata?.set_id || shape.attrs?.metadata?.set_id
          if (shapeSetId === setId) {
            setItemIds.add(shape.id)
          }
        })
      })

      // Create a map of updates for quick lookup
      const updateMap = new Map(list.map(item => [item.id, item.attrs]))

      const tempList = this.shapeList.map((shape) => {
        const found = updateMap.get(shape.id)
        if (found) {
          // Item is directly in the update list
          return this.getUpdatedItem(shape, found)
        } else if (setItemIds.has(shape.id)) {
          // Item is in a set that's being updated - find the first item in the set that's being updated
          const setId = shape.metadata?.set_id || shape.attrs?.metadata?.set_id
          if (setId) {
            const setUpdateItem = list.find(item => {
              const itemShape = this.shapeList.find(s => s.id === item.id)
              const itemSetId = itemShape?.metadata?.set_id || itemShape?.attrs?.metadata?.set_id
              return itemSetId === setId
            })
            if (setUpdateItem) {
              // Apply non-spatial attributes to all items in the set
              const { x, y, rotation, ...otherAttrs } = setUpdateItem.attrs as any
              const nonSpatialAttrs: any = { ...otherAttrs }
              if (nonSpatialAttrs.attrs) {
                const { rotation: _r, ...restAttrs } = nonSpatialAttrs.attrs
                nonSpatialAttrs.attrs = restAttrs
              }
              if (nonSpatialAttrs.metadata?.shape_id) {
                const { shape_id: _s, ...restMeta } = nonSpatialAttrs.metadata
                nonSpatialAttrs.metadata = restMeta
              }
              if (nonSpatialAttrs.attrs?.metadata?.shape_id) {
                const { shape_id: _s, ...restMeta } = nonSpatialAttrs.attrs.metadata
                nonSpatialAttrs.attrs = {
                  ...nonSpatialAttrs.attrs,
                  metadata: restMeta,
                }
              }
              return this.getUpdatedItem(shape, nonSpatialAttrs)
            }
          }
        }
        return shape
      })

      this.shapeList = tempList
      this.updateShapeList3D()

      // Update selectedItemsOnGrid to reference the new shape objects
      this.selectedItemsOnGrid = this.selectedItemsOnGrid.map(selectedItem => {
        const updatedShape = this.shapeList.find(shape => shape.id === selectedItem.id)
        return updatedShape || selectedItem
      })

      this.isProcessing = false
    },

    setBgColor(color: string) {
      this.gridBgColor = color
    },

    setGridSize(width: number, height: number) {
      this.gridWidth = width
      this.gridHeight = height
    },

    /** Expand the grid if any shape extends beyond its current bounds */
    expandGridToFitShapes() {
      if (!this.shapeList.length) return
      const offset = this.startOffset
      let maxRight = this.gridWidth
      let maxBottom = this.gridHeight

      for (const shape of this.shapeList) {
        const x = (shape.x || 0) - offset
        const y = (shape.y || 0) - offset
        const w = shape.width || shape.w || 0
        const h = shape.height || shape.h || 0
        const right = x + w
        const bottom = y + h
        if (right > maxRight) maxRight = right
        if (bottom > maxBottom) maxBottom = bottom
      }

      if (maxRight > this.gridWidth || maxBottom > this.gridHeight) {
        // Snap to grid increments
        const snapX = this.gridPattern?.snap?.x || 60
        const snapY = this.gridPattern?.snap?.y || 60
        const newWidth = Math.ceil(maxRight / snapX) * snapX
        const newHeight = Math.ceil(maxBottom / snapY) * snapY
        this.setGridSize(newWidth, newHeight)
      }
    },

    /**
     * Grow the grid so a (possibly multi-tile) shape fits at the cursor's
     * drop position. Grows toward the cursor — right if cursor is past the
     * right edge, down if past the bottom edge, both if both. Growth that
     * would exceed MAX_GRID_*_PX is clamped and the result is flagged
     * `capped: true` so the caller can show a "grid is full" toast.
     *
     * `cells` must be in grid-relative coordinates (subtract startOffset).
     * `cursorAbs` is in absolute canvas coordinates. `growthStep` is
     * optional — pass a custom step (e.g. leader tile size) to size the
     * growth to the shape. Default is 480px (4ft).
     */
    autoExpandGridForPlacement(
      cells: PlacementFootprintCell[],
      cursorAbs: { x: number; y: number },
      growthStep?: { x: number; y: number },
    ): GridGrowthResult {
      const snap = this.gridPattern?.snap || { x: 60, y: 60 }
      const result = computeRequiredGridSize(cells, cursorAbs, {
        gridWidth: this.gridWidth,
        gridHeight: this.gridHeight,
        startOffset: this.startOffset,
        snap,
        is45Square: this.gridPattern?.id === GRID_PATTERN_IDS.SQUARE_45,
        isLayered: isLayeredShape(this.selectedShape),
        maxGridWidth: MAX_GRID_WIDTH_PX,
        maxGridHeight: MAX_GRID_HEIGHT_PX,
        growthStep,
      })

      if (result.needsExpansion && !result.capped) {
        this.setGridSize(result.newWidth, result.newHeight)
      }

      return result
    },

    // Grid edge expansion actions
    setHoveredEdges(edges: string[]) {
      this.hoveredEdges = edges
    },

    startGridResize(mousePos: Vector2d) {
      this.isResizingGrid = true
      this.resizeStartPos = { ...mousePos }
      this.resizeStartSize = { width: this.gridWidth, height: this.gridHeight }
    },

    endGridResize() {
      this.isResizingGrid = false
      this.resizeStartPos = { x: 0, y: 0 }
      this.resizeStartSize = { width: 0, height: 0 }
    },

    setTempNewItems(items: ShapeInterface[]) {
      this.tempNewItems = items
    },

    updateShapeList3D() {
      if (this.gridPattern?.id !== GRID_PATTERN_IDS.THREE_D) {
        this.shapeList3D = []
        return []
      }

      this.shapeList3D = sort3dShapeList(this.shapeList).map(item => get3dShapeGroup(item))
      return this.shapeList3D
    },

    setShowDepthPreview(value: boolean) {
      this.showDepthPreview = value
      this.updateShapeList3D()
    },

    setTempItemByCurrentSelection() {
      const requestRevision = ++this.tempRequestRevision
      if (!this.selectedColor || !this.selectedShape) {
        throw new Error('No color or shape selected')
      }

      // Create a key to identify if the temp item config has changed
      const quantity = this.selectedShape.quantity || 1
      const configKey = `${this.selectedShape.id}-${this.selectedColor.label}-${this.selectedRotation || 0}-${getVariationCatalogRevision()}-${this.layeredBottomColor?.label || ""}-${this.layeredBottomColor?.hex || ""}`
      const baseMargin = this.gridPattern?.snap.x || 30
      // 18mm layered tiles on the 45° grid are drawn rotated 45°, so their
      // visible bounding box is shapeSize * √2. Bump the margin to shapeSize *
      // (√2 - 1) so adjacent tiles don't overlap at their corners. Scoped
      // strictly to the 45° grid + layered shape combination.
      const is45Square = this.gridPattern?.id === GRID_PATTERN_IDS.SQUARE_45
      const layeredOverlapMargin = is45Square && isLayeredShape(this.selectedShape)
        ? (this.selectedShape.w || 0) * (Math.sqrt(2) - 1)
        : 0
      const margin = baseMargin + layeredOverlapMargin

      // If we have a cached temp item with the same config, use it immediately
      if (this.tempItemCache && this.lastTempItemKey === configKey) {
        const isTurned = Math.abs((this.tempItemCache.rotation || 0) % 180) === 90
        const shapeWidth = isTurned
          ? (this.tempItemCache.height || this.selectedShape.h)
          : (this.tempItemCache.width || this.selectedShape.w)
        const shapeHeight = isTurned
          ? (this.tempItemCache.width || this.selectedShape.w)
          : (this.tempItemCache.height || this.selectedShape.h)
        const baseX = this.tempItemCache.x
        const baseY = this.tempItemCache.y

        // Calculate positions for multiple shapes
        const positions = calculateMultiShapePositions(
          quantity,
          baseX,
          baseY,
          shapeWidth,
          shapeHeight,
          margin
        )

        const cachedItems: ShapeInterface[] = positions.map((pos, index) => ({
          ...this.tempItemCache!,
          id: `${Math.round(Math.random() * 10000)}-${index}`,
          x: pos.x,
          y: pos.y,
        })) as ShapeInterface[]

        if(this.gridPattern?.id === GRID_PATTERN_IDS.THREE_D){
          this.setTempNewItems(sort3dShapeList(cachedItems))
        } else {
          this.setTempNewItems(cachedItems)
        }
        return
      }

      const baseTempItem = getShapeBaseConfig(this.selectedShape, this.selectedColor, true, !isLayeredShape(this.selectedShape),
        this.layeredBottomColor ? { metadata: { layered_engraving: { bottom_color: this.layeredBottomColor.label, bottom_hex: this.layeredBottomColor.hex } } } : undefined) as any
      if (!baseTempItem) return

      baseTempItem.rotation = normalizeShapeRotation(this.selectedShape, this.selectedRotation ?? baseTempItem.rotation)

      // On the 45° grid every rotation is the user's angle PLUS the grid's
      // own 45° turn - the shape list encodes it that way (`rotation: 45`
      // is the 0° entry, `rotation: 90 + 45` is the 90° entry). So a chosen
      // angle has to have the turn added on top, otherwise the tile lands
      // at the bare angle and shows the square grid's upright orientation
      // instead of the 45° one.
      //
      // With no angle chosen the shape's own rotation is already right, so
      // it is used as-is.
      if (this.gridPattern?.id === GRID_PATTERN_IDS.SQUARE_45 && this.selectedRotation != null && !isRotationLocked(this.selectedShape)) {
        baseTempItem.rotation = this.selectedRotation + 45
      }

      // Apply 45° rotation for shapes on the 45° grid that don't have built-in rotation
      if (this.gridPattern?.id === GRID_PATTERN_IDS.SQUARE_45 && !this.selectedShape.rotation && !this.selectedRotation) {
        baseTempItem.rotation = 45
      }

      // 18mm layered children carry the unrotated 0/90/180/270 rotation, but on
      // the 45° grid they need the +45° offset to render as the rotated design.
      if (this.gridPattern?.id === GRID_PATTERN_IDS.SQUARE_45 && isLayeredShape(this.selectedShape)) {
        const baseRotation = this.selectedRotation ?? this.selectedShape.rotation ?? 0
        baseTempItem.rotation = baseRotation + 45
      }

      // Get shape dimensions for positioning
      const isTurned = Math.abs((baseTempItem.rotation || 0) % 180) === 90
      const shapeWidth = isTurned
        ? (baseTempItem.height || this.selectedShape.h)
        : (baseTempItem.width || this.selectedShape.w)
      const shapeHeight = isTurned
        ? (baseTempItem.width || this.selectedShape.w)
        : (baseTempItem.height || this.selectedShape.h)

      // Calculate positions for multiple shapes
      const positions = calculateMultiShapePositions(
        quantity,
        baseTempItem.x,
        baseTempItem.y,
        shapeWidth,
        shapeHeight,
        margin
      )

      // Create multiple temp items if quantity > 1
      const createTempItems = (usePlaceholder: boolean) => {
        return positions.map((pos, index) => {
          const item = {
            ...baseTempItem,
            id: `${Math.round(Math.random() * 10000)}-${index}`,
            x: pos.x,
            y: pos.y,
          }

          if (usePlaceholder) {
            // Use solid color as placeholder while image loads
            item.fill = baseTempItem.fill || '#cccccc'
            item.fillPatternImage = null // Remove pattern temporarily for instant display
          }

          return item
        })
      }

      // IMMEDIATE FEEDBACK: Always show temp items immediately, even with placeholder
      const placeholderTempItems = createTempItems(true)
      this.setTempNewItems(placeholderTempItems)

      // Check if image is already loaded to avoid delay
      if (baseTempItem.fillPatternImage && baseTempItem.fillPatternImage.complete && baseTempItem.fillPatternImage.naturalWidth > 0) {
        // Image is already loaded, update immediately with pattern and cache
        const finalTempItems = createTempItems(false)
        this.setTempNewItems(finalTempItems)
        this.tempItemCache = { ...baseTempItem }
        this.lastTempItemKey = configKey
      } else if (baseTempItem.fillPatternImage) {
        // Client-side: wait for image to load, then update with pattern
        baseTempItem.fillPatternImage.onload = () => {
          if (requestRevision !== this.tempRequestRevision) return
          const finalTempItems = createTempItems(false)
          this.setTempNewItems(finalTempItems)
          this.tempItemCache = { ...baseTempItem }
          this.lastTempItemKey = configKey
        }
      } else {
        // No pattern image needed, cache the placeholder as final
        this.tempItemCache = { ...baseTempItem }
        this.lastTempItemKey = configKey
      }
    },

    setLassoSelectionItem(item: Line | null) {
      this.lassoSelectionItem = item
    },

    setRectSelectionItem(item: Line | null) {
      this.rectSelectionItem = item
    },

    resetGrid() {
      this.gridPos = { x: 0, y: 0 }
      this.gridZoom = 1
    },

    addShape(shape: ShapeInterface) {
      this.shapeList.push(shape)
      this.updateShapeList3D()
    },

    // PERFORMANCE: Batch add multiple shapes to reduce reactivity overhead
    addShapes(shapes: ShapeInterface[]) {
      this.shapeList.push(...shapes)
      this.updateShapeList3D()
    },

    cloneShapeList(shapeList: ShapeInterface[]): ShapeInterface[] {
      return shapeList.map(shape => {
        // Create a deep clone of the shape, excluding fillPatternImage which needs special handling
        const clonedShape = {
          ...shape,
          attrs: shape.attrs ? { ...shape.attrs } : undefined,
          metadata: shape.metadata ? { ...shape.metadata } : undefined,
        }

        // Preserve the fillPatternImage reference as-is since it's an Image object
        if (shape.fillPatternImage) {
          clonedShape.fillPatternImage = shape.fillPatternImage
        }
        if (shape.attrs?.fillPatternImage) {
          clonedShape.attrs.fillPatternImage = shape.attrs.fillPatternImage
        }

        return clonedShape
      })
    },

    saveState() {
      this.undoStack.push(this.cloneShapeList(this.shapeList))
      this.redoStack = []
    },

    async undo() {
      if (this.undoStack.length === 0) return

      // Store current selection IDs before undo
      const selectedIds = this.selectedItemsOnGrid.map(item => item.id)

      this.redoStack.push(this.cloneShapeList(this.shapeList))
      const restoredShapes = this.undoStack.pop() || []

      // Transform shapes to regenerate fillPatternImage objects
      this.shapeList = await Promise.all(
        restoredShapes.map(async (shape: ShapeInterface) => transformShapeForAdd(shape))
      ) as ShapeInterface[]
      this.updateShapeList3D()

      // Find shapes that were restored (newly added back)
      const currentShapeIds = this.shapeList.map(shape => shape.id)
      const previousShapeIds = this.redoStack.length > 0 ?
        this.redoStack[this.redoStack.length - 1].map(shape => shape.id) : []

      const restoredShapeIds = currentShapeIds.filter(id => !previousShapeIds.includes(id))

      // Select restored shapes if any, otherwise restore previous selection
      if (restoredShapeIds.length > 0) {
        this.selectItemsOnGrid(restoredShapeIds)
      } else {
        this.restoreSelection(selectedIds)
      }

      // Save the changes to the backend
      const projectStore = useProjectStore()
      projectStore.save(false, true) // Don't update URL, skip toast
    },

    async redo() {
      if (this.redoStack.length === 0) return

          // Store current selection IDs before redo
          const selectedIds = this.selectedItemsOnGrid.map(item => item.id)

      this.undoStack.push(this.cloneShapeList(this.shapeList))
      const restoredShapes = this.redoStack.pop() || []

      // Transform shapes to regenerate fillPatternImage objects
      this.shapeList = await Promise.all(
        restoredShapes.map(async (shape: ShapeInterface) => transformShapeForAdd(shape))
      ) as ShapeInterface[]
      this.updateShapeList3D()

          // Find shapes that were restored (newly added back)
          const currentShapeIds = this.shapeList.map(shape => shape.id)
          const previousShapeIds = this.undoStack.length > 0 ?
            this.undoStack[this.undoStack.length - 1].map(shape => shape.id) : []

          const restoredShapeIds = currentShapeIds.filter(id => !previousShapeIds.includes(id))

          // Select restored shapes if any, otherwise restore previous selection
          if (restoredShapeIds.length > 0) {
            this.selectItemsOnGrid(restoredShapeIds)
          } else {
            this.restoreSelection(selectedIds)
          }

      // Save the changes to the backend
      const projectStore = useProjectStore()
      projectStore.save(false, true) // Don't update URL, skip toast
    },

    async transformItemsFromBundle() {
      const bundlesBySetId = new Map<string, ShapeInterface[]>()
      const allTransformedItems: ShapeInterface[] = []
      const baseMargin = this.gridPattern?.snap.x || 30
      // 18mm layered tiles on the 45° grid need extra spacing so their rotated
      // bounding boxes don't overlap. Use the largest required margin across
      // all selected items so bundled transforms stay correct.
      let layeredOverlapMargin = 0
      if (this.gridPattern?.id === GRID_PATTERN_IDS.SQUARE_45) {
        for (const item of this.selectedItemsOnGrid) {
          const shape = getShapeById(item.metadata?.shape_id || item.attrs?.metadata?.shape_id)
          if (shape && isLayeredShape(shape) && shape.w) {
            const candidate = shape.w * (Math.sqrt(2) - 1)
            if (candidate > layeredOverlapMargin) layeredOverlapMargin = candidate
          }
        }
      }
      const margin = baseMargin + layeredOverlapMargin

      this.selectedItemsOnGrid.forEach(item => {
        const setId = item.metadata?.set_id || item.attrs?.metadata?.set_id
        if (setId) {
          if (!bundlesBySetId.has(setId)) {
            bundlesBySetId.set(setId, [])
          }
          bundlesBySetId.get(setId)!.push(item)
        }
      })

      // Process each unique bundle
      for (const [setId, selectedItemsInSet] of bundlesBySetId) {
        // Find the first selected item from this set (leader)
        const leaderItem = selectedItemsInSet[0]
        if (!leaderItem) continue

        // Find all items in the original set (not just selected ones)
        const allItemsInSet = this.shapeList.filter(shape => {
          const shapeSetId = shape.metadata?.set_id || shape.attrs?.metadata?.set_id
          return shapeSetId === setId
        })

        if (allItemsInSet.length === 0) continue

        // Get shape definition from leader to get quantity
        const leaderMetadata = leaderItem.metadata || leaderItem.attrs?.metadata
        if (!leaderMetadata?.shape_id) continue

        const shapeDef = getShapeById(leaderMetadata.shape_id)
        if (!shapeDef) continue

        const quantity = shapeDef.quantity || allItemsInSet.length

        // Get leader's position and shape dimensions
        const leaderX = leaderItem.x
        const leaderY = leaderItem.y
        const shapeWidth = leaderItem.width || shapeDef.w
        const shapeHeight = leaderItem.height || shapeDef.h

        // Calculate positions for items based on quantity pattern
        const basePositions = calculateMultiShapePositions(
          quantity,
          leaderX,
          leaderY,
          shapeWidth,
          shapeHeight,
          margin
        )

        // If we have more items than positions, we need to generate more positions
        // by continuing the pattern or using the base positions cyclically
        const positions: Array<{ x: number; y: number }> = []
        if (allItemsInSet.length <= basePositions.length) {
          positions.push(...basePositions.slice(0, allItemsInSet.length))
        } else {
          // Use base positions and continue the pattern for extra items
          positions.push(...basePositions)
          // For extra items, continue the pattern by adding to the last position
          const lastPos = basePositions[basePositions.length - 1]
          for (let i = basePositions.length; i < allItemsInSet.length; i++) {
            const col = (i - basePositions.length) % 4
            const row = Math.floor((i - basePositions.length) / 4)
            positions.push({
              x: leaderX + col * (shapeWidth + margin),
              y: leaderY + row * (shapeHeight + margin)
            })
          }
        }

        // Create new set_id for the copied bundle
        // Include original setId in the new ID to ensure uniqueness when multiple sets are copied
        const newSetId = `set-${Date.now()}-${setId}-${Math.round(Math.random() * 10000)}`

        // Transform all items in the set and assign new positions
        const transformedSetItems = await Promise.all(
          allItemsInSet.map(async (item, index) => {
            const transformed = await transformShapeForAdd(item)
            const position = positions[index] || { x: leaderX, y: leaderY }

            // Update metadata with new set_id
            const existingMetadata = transformed.metadata || transformed.attrs?.metadata
            const updatedMetadata = existingMetadata
              ? { ...existingMetadata, set_id: newSetId }
              : { set_id: newSetId }

            return {
              ...transformed,
              x: position.x,
              y: position.y,
              metadata: updatedMetadata,
              attrs: {
                ...transformed.attrs,
                metadata: updatedMetadata,
              }
            } as ShapeInterface
          })
        )

        allTransformedItems.push(...transformedSetItems)
      }

      return allTransformedItems
    },

    async copySelectedItems() {
      const nonBundleItems: ShapeInterface[] = []

      this.selectedItemsOnGrid.forEach(item => {
        // Items without set_id are non-bundle items
        if (!item.metadata?.set_id && !item.attrs?.metadata?.set_id) {
          nonBundleItems.push(item)
        }
      })

      const allTransformedItems: ShapeInterface[] = await this.transformItemsFromBundle()

      // Transform non-bundle items normally
      const transformedNonBundleItems = await Promise.all(
        nonBundleItems.map(async (item: ShapeInterface) => transformShapeForAdd(item))
      ) as ShapeInterface[]

      allTransformedItems.push(...transformedNonBundleItems)

      this.clipboard = {
        action: 'copy',
        items: allTransformedItems,
      }
      this.setTempNewItems(this.clipboard.items)
    },

    async cutSelectedItems() {
      // Transform shapes to regenerate fillPatternImage objects for clipboard
      const transformedItems = await Promise.all(
        this.selectedItemsOnGrid.map(async (item: ShapeInterface) => transformShapeForAdd(item))
      ) as ShapeInterface[]

      // Store in clipboard
      this.clipboard = {
        action: 'cut',
        items: transformedItems,
      }

      // Delete the selected items from the grid immediately
      const selectedIds = this.selectedItemsOnGrid.map(item => item.id)
      this.saveState()
      this.shapeList = this.shapeList.filter(shape => !selectedIds.includes(shape.id))
      this.updateShapeList3D()
      this.selectItemsOnGrid([])

      // Set clipboard items as temp (ready to paste)
      this.setTempNewItems(this.clipboard.items)
    },

    pasteItems(itemIds: string[]) {
      const newItemIds = itemIds || this.clipboard.items.map(item => item.id)

      this.selectedTool = SELECTABLE_TOOLS.SELECT
      this.selectItemsOnGrid(newItemIds)
      this.clearClipboard()
      this.setTempNewItems([])
    },

    clearClipboard() {
      this.clipboard = {
        action: null,
        items: [],
      }
    },

    async updateAuthorEmail(email: string) {
      this.authorEmail = email;
    },
    async transformShapeForAdd(shape: ShapeInterface) {
      if (!shape.metadata) throw new Error('Unable to transform. Metadata is required')

      function getShapeId(obj: any) {
        if (obj.attrs && obj.attrs.metadata) return getShapeId(obj.attrs)
        if (obj.metadata) return obj.metadata?.shape_id
        return obj.id
      }

      function getColorLabel(obj: any) {
        if (obj.attrs && obj.attrs.metadata) return getColorLabel(obj.attrs)
        if (obj.metadata) return obj.metadata?.color_label
        return obj.color_label
      }

      const shapeId = getShapeId(shape)
      const colorLabel = getColorLabel(shape)
      const shapeBase = getShapeById(shapeId)
      const colorBase = useStudioColors().getColors().find((c: ColorChoice) => c.label === colorLabel)

      if (!shapeBase || !colorBase) throw new Error('Unable to transform. Shape and Color are required')

      const result = getShapeBaseConfig(shapeBase, colorBase, true) as any
      console.table({ shapeId, colorLabel, shapeBase, colorBase, result })
      if (!result) throw new Error('Unable to transform. Shape Config unavailable')

      result.id = shape.id
      result.x = shape.x
      result.y = shape.y
      result.rotation = shape.rotation

      // Preserve original metadata (including set_id) by merging with new metadata
      // Metadata can be in shape.metadata or shape.attrs.metadata
      const originalMetadata = shape.metadata || shape.attrs?.metadata
      const newMetadata = result.metadata || result.attrs?.metadata

      if (originalMetadata) {
        // Merge original metadata with new metadata, preserving set_id and other original fields
        const mergedMetadata = newMetadata
          ? { ...originalMetadata, ...newMetadata }
          : originalMetadata

        // Set metadata in both locations to ensure compatibility
        result.metadata = mergedMetadata
        if (result.attrs) {
          result.attrs.metadata = mergedMetadata
        }
      }

      // Client-side: wait for image to load
      return new Promise((resolve, _) => {
        result.fillPatternImage.onload = () => {
          const resolved = {
            ...result,
            attrs: {
              ...result.attrs,
              rotation: shape.rotation
            }
          }
          return resolve(resolved)
        }
      })
    },
    async loadDesign(design: any) {
      try {
        const { width, height, offset, backgroundColor, pattern } = design.grid_properties
        this.setGridPattern(GRID_PATTERN_LIST.find(p => p.id === pattern) || GRID_PATTERN_LIST[DEFAULT_GRID_PATTERN])
        this.setGridSize(width, height)
        this.startOffset = offset
        this.gridBgColor = backgroundColor;
        this.shapeList = (await Promise.all(
          design.shapes.map(
            async (shape: ShapeInterface) => {
              try {
                return await transformShapeForAdd(shape)
              } catch (e) {
                console.warn('Failed to transform shape, skipping:', shape.metadata?.shape_id, e)
                return null
              }
            }
          )
        )).filter(Boolean) as ShapeInterface[]

        if (pattern === GRID_PATTERN_IDS.THREE_D) {
          this.updateShapeList3D()
        }

        // Clear undo/redo stacks when loading a new design
        this.undoStack = []
        this.redoStack = []

        window.dispatchEvent(new Event('designLoaded'))
      } catch (e: any) {
        console.warn(e)
        throw new Error('Unable to load design')
      }
    },
  },
})
